import React, { useState, useEffect } from 'react';
import { useQCM } from '../../contexts/QCMContext';
import { useAuth } from '../../contexts/AuthContext';

const RoomForImprovement = () => {
    const { currentQCM, sessionContext } = useQCM();
    const { user } = useAuth();
    const [selectedConcept, setSelectedConcept] = useState(null);
    const [improvementList, setImprovementList] = useState([]);

    // Utiliser les données locales au lieu de l'API
    useEffect(() => {
        console.log('🔍 DEBUG RoomForImprovement - Using local improvement_list data');
        console.log('🔍 DEBUG - currentQCM:', currentQCM);
        console.log('🔍 DEBUG - currentQCM.improvement_list:', currentQCM?.improvement_list);
        
        if (currentQCM?.improvement_list && Array.isArray(currentQCM.improvement_list)) {
            console.log('🔍 DEBUG - Setting improvement list from local data:', currentQCM.improvement_list);
            setImprovementList(currentQCM.improvement_list);
        } else {
            console.log('🔍 DEBUG - No local improvement_list found, setting empty array');
            setImprovementList([]);
        }
    }, [currentQCM?.improvement_list]);

    // Gérer le clic sur un concept
    const handleConceptClick = (concept, definition) => {
        if (selectedConcept && selectedConcept.concept === concept) {
            setSelectedConcept(null); // Fermer si déjà ouvert
        } else {
            setSelectedConcept({ concept, definition }); // Ouvrir le nouveau concept
        }
    };

    // Parser les éléments d'amélioration
    const parseImprovementItem = (item) => {
        if (typeof item === 'string' && item.includes('||')) {
            const [concept, definition] = item.split('||').map(part => part.trim());
            return { concept, definition };
        }
        return { concept: item, definition: '' };
    };

    // Sauvegarder une sélection (garder la logique existante)
    const handleItemSelect = async (item) => {
        if (!currentQCM?.qcm?._id || !sessionContext?.exam || !sessionContext?.sessionId || !user?.username) {
            console.error('Missing required context for saving selection');
            return;
        }

        try {
            const selectionData = {
                exam: sessionContext.exam,
                session: sessionContext.sessionId,
                user: user.username,
                qcm_id: currentQCM.qcm._id,
                qcm_metadata: {
                    theme: currentQCM.qcm.theme,
                    context: currentQCM.qcm.context,
                    question: currentQCM.qcm.question,
                    correct_answer: currentQCM.qcm.correct_answer,
                    explanation: currentQCM.qcm.explanation
                },
                selected_value: item,
                timestamp: new Date().toISOString()
            };

            const response = await fetch(`/api/improvement/save`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(selectionData)
            });
            const data = await response.json();
            
            if (data.success) {
                console.log('Selection saved successfully');
            } else {
                console.error('Failed to save selection:', data.error);
            }
        } catch (err) {
            console.error('Error saving selection:', err);
        }
    };

    console.log('🔍 DEBUG RoomForImprovement - Rendering component');
    console.log('🔍 DEBUG - improvementList:', improvementList);
    console.log('🔍 DEBUG - selectedConcept:', selectedConcept);

    if (!improvementList || improvementList.length === 0) {
        return (
            <div className="bg-blue-50/10 border border-blue-200/20 rounded-lg p-4">
                <div className="flex items-center mb-3">
                    <svg className="w-5 h-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-blue-400">Room for Improvement</h3>
                </div>
                <p className="text-gray-400 text-sm">No improvement areas available for this question.</p>
            </div>
        );
    }

    return (
        <div className="bg-blue-50/10 border border-blue-200/20 rounded-lg p-4">
            <div className="flex items-center mb-3">
                <svg className="w-5 h-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <h3 className="text-lg font-semibold text-blue-400">Room for Improvement</h3>
            </div>
            
            <p className="text-gray-400 text-sm mb-4">Select the technical concepts you need to improve:</p>
            
            {/* Tags des concepts */}
            <div className="flex flex-wrap gap-2 mb-4">
                {improvementList.map((item, index) => {
                    const { concept, definition } = parseImprovementItem(item);
                    const isSelected = selectedConcept && selectedConcept.concept === concept;
                    
                    return (
                        <button
                            key={index}
                            onClick={() => handleConceptClick(concept, definition)}
                            className={`px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                                isSelected
                                    ? 'bg-blue-600 text-white border-2 border-blue-400'
                                    : 'bg-gray-700 text-blue-300 border-2 border-blue-500/30 hover:bg-blue-600/20 hover:border-blue-400'
                            }`}
                        >
                            {concept}
                        </button>
                    );
                })}
            </div>
            
            {/* Ligne commune pour afficher la définition */}
            {selectedConcept && (
                <div className="bg-gray-800 rounded-lg border border-blue-500/30 p-3 mb-4">
                    <div className="flex items-center">
                        <span className="text-blue-400 font-medium mr-2 text-sm">
                            {selectedConcept.concept}:
                        </span>
                        <p className="text-gray-300 text-sm leading-relaxed flex-1">
                            {selectedConcept.definition}
                        </p>
                    </div>
                </div>
            )}
            
            {/* Indication visuelle */}
            <div className="text-xs text-blue-400/70">
                💡 Click concept to see definition
            </div>
        </div>
    );
};

export default RoomForImprovement; 